import { Injectable, inject, computed, PLATFORM_ID } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { environment as env } from '@env/environment';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { RouteData } from 'src/app/core/models/Navigation';
import { SettingsService } from './settings/settings.service';
import { PageResolverData } from '@app/interfaces/page/page';

export interface SeoConfig {
  title?: string;
  titleSuffix?: string;
  description?: string;
  image?: string;
  keywords?: string;
  url?: string;
  type?: string;
  siteName?: string;
  author?: string;
  canonical?: string;
  robots?: string;
  locale?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
}

export interface MetaProperty {
  property: string;
  itemprop?: string;
  content: string;
}

export interface MetaName {
  name: string;
  itemprop?: string;
  content: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly baseUrl = env.baseUrl;
  private readonly settingsService = inject(SettingsService);
  // private readonly serverLogger = inject(ServerLogTransferService);

  // Computed signal that always gets the latest agency name
  private readonly agencyName = computed(() => this.settingsService.agencyName() || 'GattinoniB2B2C');

  initialize(): void {
    // Apply SEO tags for the current route immediately during initialization
    // This is critical for SSR to render with correct meta tags
    try {
      let snapshot = this.activatedRoute.snapshot;
      while (snapshot.firstChild !== null) {
        snapshot = snapshot.firstChild;
      }
      const routeData = snapshot.data as RouteData;
      this.applySeoTags(routeData);
    } catch (error) {
      console.warn('SEO initialize: unable to apply initial route SEO', error);
    }

    // Subscribe to future navigation events to keep SEO in sync
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map(() => {
          let snapshot = this.router.routerState.snapshot.root;
          while (snapshot.firstChild !== null) {
            snapshot = snapshot.firstChild;
          }
          return snapshot.data as RouteData;
        }),
        distinctUntilChanged((prev, curr) => {
          if (prev?.key === undefined || curr?.key === undefined) {
            return false;
          }
          return prev.key === curr.key;
        })
      )
      .subscribe(routeData => {
        this.applySeoTags(routeData);
      });
  }

  private applySeoTags(data: any): void {
    // Get the current agency name from the signal
    const agencyName = this.agencyName();

    const dynSeo: PageResolverData | undefined = data?.seoData;

    let config: SeoConfig;

    if (!!dynSeo) {
      config = {
        title: dynSeo?.title ?? data.title ?? '',
        titleSuffix: dynSeo?.addTitleSuffix ? agencyName : undefined,
        description: dynSeo?.description ?? data.description,
        image: dynSeo?.image ?? data.image,
        keywords: data.keywords ? data.keywords.join(', ') : '',
        url: dynSeo?.url ?? this.router.url, // Just the route path, e.g., "/"
        type: 'website',
        siteName: agencyName,
        locale: 'it_IT',
        twitterCard: 'summary_large_image',
        robots: 'index, follow',
      };
    } else {
      config = {
        title: data.title ?? '',
        titleSuffix: agencyName,
        description: data.description,
        image: data.image,
        keywords: data.keywords ? data.keywords.join(', ') : '',
        url: this.router.url, // Just the route path, e.g., "/"
        type: 'website',
        siteName: agencyName,
        locale: 'it_IT',
        twitterCard: 'summary_large_image',
        robots: 'index, follow',
      };
    }

    this.setSeoTags(config);
  }

  setSeoTags(config: SeoConfig): void {
    // Set page title
    if (config.title && config.titleSuffix) {
      this.setTitle(config.titleSuffix, config.title);
    } else if (config.title || config.titleSuffix) {
      this.setTitle(config.titleSuffix || config.title!);
    }

    if (config.description) {
      this.setDescription(config.description);
    }

    if (config.image) {
      this.setImage(config.image);
    }

    if (config.keywords) {
      this.setKeywords(config.keywords);
    }

    if (config.url) {
      this.setUrl(config.url);
    }

    this.setAdditionalMetaTags(config);
  }

  private setAdditionalMetaTags(config: SeoConfig): void {
    // Open Graph type
    if (config.type) {
      this.setPropertyTag({ property: 'og:type', content: config.type });
    }

    // Site name
    if (config.siteName) {
      this.setPropertyTag({ property: 'og:site_name', content: config.siteName });
    }

    // Locale
    if (config.locale) {
      this.setPropertyTag({ property: 'og:locale', content: config.locale });
    }

    // Twitter card type
    if (config.twitterCard) {
      this.setNameTag({ name: 'twitter:card', content: config.twitterCard });
    }

    // Author
    if (config.author) {
      this.setNameTag({ name: 'author', content: config.author });
    }

    // Robots
    if (config.robots) {
      this.setNameTag({ name: 'robots', content: config.robots });
    }

    // Viewport (important for mobile SEO)
    this.setNameTag({
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0',
    });
  }

  setNameTag(metaName: MetaName): void {
    const { name, content, itemprop = '' } = metaName;

    const metaDefinition: MetaDefinition = {
      name,
      content,
      ...(itemprop && { itemprop }),
    };

    if (this.meta.getTag(`name="${name}"`)) {
      this.meta.updateTag(metaDefinition);
    } else {
      this.meta.addTag(metaDefinition);
    }
  }

  setNameTags(names: MetaName[]): void {
    names.forEach(metaName => this.setNameTag(metaName));
  }

  private setPropertyTag(metaProperty: MetaProperty): void {
    const { property, content, itemprop = '' } = metaProperty;

    const metaDefinition: MetaDefinition = {
      property,
      content,
      ...(itemprop && { itemprop }),
    };

    if (this.meta.getTag(`property="${property}"`)) {
      this.meta.updateTag(metaDefinition);
    } else {
      this.meta.addTag(metaDefinition);
    }
  }

  setUrl(url: string): void {
    this.setPropertyTag({
      property: 'og:url',
      itemprop: 'url',
      content: url,
    });

    this.setPropertyTag({
      property: 'twitter:url',
      content: url,
    });
  }

  setTitle(titleSuffix: string, title?: string): void {
    const pageTitle = title && title.trim() !== '' ? `${title} - ${titleSuffix}` : titleSuffix;

    this.title.setTitle(pageTitle);

    this.setPropertyTag({
      property: 'og:title',
      itemprop: 'title',
      content: pageTitle,
    });
    this.setPropertyTag({
      property: 'twitter:title',
      content: pageTitle,
    });
  }

  setDescription(content: string): void {
    const description = content.length > 160 ? content.substring(0, 157) + '...' : content;

    this.setNameTag({
      name: 'description',
      itemprop: 'description',
      content: description,
    });

    this.setPropertyTag({
      property: 'og:description',
      itemprop: 'description',
      content: description,
    });

    this.setPropertyTag({
      property: 'twitter:description',
      content: description,
    });
  }

  setImage(imageUrl: string): void {
    const absoluteImageUrl = imageUrl.startsWith('http') ? imageUrl : `${this.baseUrl}${imageUrl}`;

    this.setPropertyTag({
      property: 'og:image',
      itemprop: 'image',
      content: absoluteImageUrl,
    });

    this.setPropertyTag({
      property: 'og:image:secure_url',
      content: absoluteImageUrl,
    });

    this.setPropertyTag({
      property: 'twitter:image',
      content: absoluteImageUrl,
    });

    this.setPropertyTag({
      property: 'og:image:width',
      content: '1200',
    });

    this.setPropertyTag({
      property: 'og:image:height',
      content: '630',
    });
  }

  setKeywords(content: string): void {
    this.setNameTag({
      name: 'keywords',
      itemprop: 'keywords',
      content,
    });
  }

  // setSeoConfig(config: Partial<SeoConfig>): void {
  //   this.setSeoTags({
  //     type: 'website',
  //     siteName: this.agencyName(),
  //     locale: 'it_IT',
  //     twitterCard: 'summary_large_image',
  //     robots: 'index, follow',
  //     ...config,
  //   });
  // }

  // resetSeoTags(): void {
  //   const agencyName = this.agencyName();
  //   if (agencyName) {
  //     this.setTitle(agencyName);
  //   }

  //   const metaTagsToRemove = [
  //     'description',
  //     'keywords',
  //     'og:description',
  //     'og:image',
  //     'og:url',
  //     'twitter:description',
  //     'twitter:image',
  //     'twitter:url',
  //   ];

  //   metaTagsToRemove.forEach(tag => {
  //     const element = this.meta.getTag(`name="${tag}"`) || this.meta.getTag(`property="${tag}"`);
  //     if (element) {
  //       this.meta.removeTag(`name="${tag}"`);
  //       this.meta.removeTag(`property="${tag}"`);
  //     }
  //   });

  //   if (this.isBrowser) {
  //     const canonical = this.document.querySelector('link[rel="canonical"]');
  //     if (canonical) {
  //       canonical.remove();
  //     }
  //   }
  // }
}
